import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNavGuard } from '../contexts/NavGuardContext';
import { LayoutDashboard, FileText, FolderOpen, LogOut, Menu, Languages, GitBranch, DollarSign, Calculator, Wrench, CheckSquare, CalendarDays, Users, Receipt } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_job-quotes/artifacts/mtux4tav_Logo%20001%20-%20Kenscho%20Design.png';

const NAV_ITEMS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/estimates', label: 'Estimates', icon: FileText },
  { to: '/clients', label: 'Clients', icon: Users },
  { to: '/change-orders', label: 'Change Orders', icon: GitBranch },
  { to: '/invoices', label: 'Invoices', icon: Receipt },
  { to: '/job-costs', label: 'Job Costs', icon: DollarSign },
  { to: '/tasks', label: 'Tasks', icon: CheckSquare },
  { to: '/schedule', label: 'Schedule', icon: CalendarDays },
  { to: '/price-book', label: 'Price Book', icon: Calculator },
  { to: '/tools', label: 'Tools', icon: Wrench },
  { to: '/templates', label: 'Templates', icon: FolderOpen },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { guard } = useNavGuard();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [language, setLanguage] = useState('en');

  useEffect(() => {
    fetchLanguage();
  }, []);

  const fetchLanguage = async () => {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setLanguage(data.language || 'en');
      }
    } catch { /* ignore */ }
  };

  const toggleLanguage = async () => {
    const newLang = language === 'en' ? 'fr' : 'en';
    setLanguage(newLang);
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ language: newLang }),
      });
    } catch { /* ignore */ }
  };

  const handleLogout = async () => {
    const ok = await guard();
    if (!ok) return;
    await logout();
    navigate('/login');
  };

  // Intercept sidebar NavLink clicks. If the active page has registered
  // an unsaved-changes guard, pause the navigation until it resolves.
  // We always preventDefault so the guard decision (async) is authoritative.
  const handleNavClick = async (e, to) => {
    e.preventDefault();
    setSidebarOpen(false);
    const ok = await guard();
    if (ok) navigate(to);
  };

  return (
    <div className="min-h-screen bg-[#F5F6F5] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-60 bg-white border-r border-[#E6EBE8] transform transition-transform duration-200 lg:translate-x-0 lg:static ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-5 py-5 border-b border-[#E6EBE8]">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Kenscho" className="h-9 w-9 rounded-md object-cover" />
              <div>
                <p className="text-sm font-bold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>Kenscho Design</p>
                <p className="text-[10px] text-[#6B7A73] uppercase tracking-wide">Estimates</p>
              </div>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                onClick={(e) => handleNavClick(e, to)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-[rgba(45,107,104,0.1)] text-[#2D6B68]'
                      : 'text-[#6B7A73] hover:text-[#1A1D1C] hover:bg-[#F0F3F1]'
                  }`
                }
                data-testid={`nav-${label.toLowerCase()}`}
              >
                <Icon className="w-4.5 h-4.5" />
                {label}
              </NavLink>
            ))}
          </nav>

          {/* Language Toggle */}
          <div className="px-3 pb-2">
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-3 w-full px-3 py-2.5 rounded-md text-sm font-medium text-[#6B7A73] hover:text-[#1A1D1C] hover:bg-[#F0F3F1] transition-colors"
              data-testid="language-toggle"
            >
              <Languages className="w-4.5 h-4.5" />
              <span>{language === 'en' ? 'English' : 'Français'}</span>
              <span className="ml-auto text-xs bg-[#F0F3F1] px-2 py-0.5 rounded-full font-semibold text-[#2D6B68] uppercase">
                {language}
              </span>
            </button>
          </div>

          {/* User */}
          <div className="px-4 py-4 border-t border-[#E6EBE8]">
            {user && (
              <div className="flex items-center gap-3 mb-3">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#2D6B68] flex items-center justify-center text-white text-xs font-bold">
                    {user.name?.[0] || 'U'}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#1A1D1C] truncate">{user.name}</p>
                  <p className="text-xs text-[#6B7A73] truncate">{user.email}</p>
                </div>
              </div>
            )}
            <Button
              data-testid="logout-btn"
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-[#6B7A73] hover:text-[#1A1D1C] hover:bg-[#F0F3F1] text-sm"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top bar mobile */}
        <header className="lg:hidden bg-white border-b border-[#E6EBE8] px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-md text-[#6B7A73] hover:bg-[#F0F3F1]"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src={LOGO_URL} alt="" className="h-7 w-7 rounded-md object-cover" />
            <span className="text-sm font-bold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>Kenscho</span>
          </div>
          <div className="w-9" />
        </header>

        <main className="flex-1 p-6 md:p-8 max-w-[1400px] w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
